/** Formateadores y helpers numéricos del módulo Safety Stock. */

export function toNumber(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

export function formatUnits(
  value: string | number | null | undefined,
  locale: string,
): string {
  const n = toNumber(value);
  if (n == null) return '—';
  return n.toLocaleString(locale, { maximumFractionDigits: 0 });
}

export function formatDays(
  value: string | number | null | undefined,
  locale: string,
  digits = 1,
): string {
  const n = toNumber(value);
  if (n == null) return '—';
  return n.toLocaleString(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
}

/** Fracción (0.25) → "25,0 %" o "—" si null. */
export function formatPercentFraction(
  value: string | number | null | undefined,
  locale: string,
  digits = 1,
): string {
  const n = toNumber(value);
  if (n == null) return '—';
  return `${(n * 100).toLocaleString(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })} %`;
}

/** Variación con signo: "+12,3 %" / "—". */
export function formatSignedPercentFraction(
  value: string | number | null | undefined,
  locale: string,
  digits = 1,
): string {
  const n = toNumber(value);
  if (n == null) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${(n * 100).toLocaleString(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })} %`;
}

/** P11: ±15 % warning, ±30 % danger (sobre fracción). */
export function deltaVariant(
  deltaPct: string | number | null | undefined,
): 'default' | 'warning' | 'danger' {
  const n = toNumber(deltaPct);
  if (n == null) return 'default';
  const abs = Math.abs(n);
  if (abs >= 0.3) return 'danger';
  if (abs >= 0.15) return 'warning';
  return 'default';
}

export function shareOfTotal(
  part: string | number | null | undefined,
  total: string | number | null | undefined,
): number | null {
  const p = toNumber(part);
  const t = toNumber(total);
  if (p == null || t == null || t <= 0) return null;
  return p / t;
}

export const FIXED_SUMMARY_DIMENSIONS = [
  'ABC_XYZ',
  'ABC',
  'XYZ',
  'ADI_CV',
  'METHOD',
  'FAMILY',
  'SUPPLIER',
] as const;

export type FixedSummaryDimension = (typeof FIXED_SUMMARY_DIMENSIONS)[number];

export function catFieldToDimension(field: string): string {
  return field.toUpperCase(); // cat_1 -> CAT_1
}

/** Métodos del motor de safety stock (códigos canónicos). */
export const SAFETY_STOCK_METHODS = [
  'DAYS_OF_COVERAGE',
  'MAXIMUM_DEMAND_AND_LEAD_TIME',
  'VARIABLE_DEMAND',
  'VARIABLE_DEMAND_VARIABLE_LEAD_TIME',
  'PERIODIC_REVIEW',
  'INTERMITTENT_DEMAND',
] as const;

export type SafetyStockMethod = (typeof SAFETY_STOCK_METHODS)[number];

/** Etiqueta de negocio para un método del motor. */
export function formatSafetyStockMethod(
  method: string | null | undefined,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (method == null || method === '') return '—';
  const code = String(method).trim().toUpperCase();
  return t(`safetyStock.methods.${code}`, { defaultValue: code });
}

/** Clasificación de demanda (ADI/CV u otras) en lenguaje de negocio. */
export function formatDemandClassification(
  value: string | null | undefined,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (value == null || value === '') return '—';
  const code = String(value).trim().toUpperCase();
  const forecastKeyByCode: Record<string, string> = {
    SMOOTH: 'forecastReview.classificationBreakdown.adiSmooth',
    INTERMITTENT: 'forecastReview.classificationBreakdown.adiIntermittent',
    ERRATIC: 'forecastReview.classificationBreakdown.adiErratic',
    LUMPY: 'forecastReview.classificationBreakdown.adiLumpy',
  };
  const forecastKey = forecastKeyByCode[code];
  if (forecastKey) {
    const label = t(forecastKey);
    if (label !== forecastKey) return label;
  }
  return t(`safetyStock.demandClassifications.${code}`, { defaultValue: code });
}
